import type { ReactElement, KeyboardEvent } from 'react';
import type { LucideIcon } from 'lucide-react';

import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import {
  Sparkles, BookOpen, Heart, Eye, User, ChevronRight,
  Wand2, Share2, PenLine, Map, Moon, PawPrint, Compass, Star, Loader2, X,
  Globe, Users, Library, Volume2, Palette, ImageIcon, Camera, Clock,
  CheckCircle, Play, Crown, Smartphone, Download, Search, UserPlus, Video, Zap
} from "lucide-react";
import Logo from "../components/Logo";
import Wordmark from "../components/Wordmark";
import TypewriterCarousel from "../components/TypewriterCarousel";
import PrintComingSoonSection from "../components/PrintComingSoonSection";
import { getBookTypeLabel } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Interfaces
interface FeaturedStory {
  id: string;
  title: string;
  thumbnail?: string;
  category: string;
  age_range: string;
  author_name?: string;
  author_picture?: string;
  favorites_count?: number;
}

interface SeriesCharacter {
  name: string;
  reference_image_url?: string;
}

interface FeaturedSeries {
  id: string;
  title: string;
  cover_image_url?: string;
  book_count?: number;
  author_name?: string;
  author_picture?: string;
  characters?: SeriesCharacter[];
}

interface Category {
  id: string;
  label: string;
  icon: LucideIcon;
  color: string;
}

interface Feature {
  icon: LucideIcon;
  title: string;
  description: string;
  color: string;
  gradient: string;
  iconBg: string;
  premium?: boolean;
}

interface HowItWorksStep {
  icon: LucideIcon;
  title: string;
  description: string;
  color: string;
}

interface SeriesFeature {
  icon: LucideIcon;
  title: string;
  description: string;
  color: string;
}

interface UserAuth {
  id: string;
  name: string;
  email: string;
}

// Helper to get full image URL
const getImageUrl = (url: string | undefined | null): string | null => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

const LandingPage = (): ReactElement => {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserAuth | null>(() => {
    try {
      const cached = localStorage.getItem("cached_user");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [featuredStories, setFeaturedStories] = useState<FeaturedStory[]>([]);
  const [featuredSeries, setFeaturedSeries] = useState<FeaturedSeries[]>([]);

  // Preview generation state
  const [promptInput, setPromptInput] = useState<string>('');
  const [previewText, setPreviewText] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<string>('');
  const [showPreview, setShowPreview] = useState<boolean>(false);

  const handlePromptSelect = (prompt: string): void => {
    setPromptInput(prompt);
  };

  const generatePreview = async (): Promise<void> => {
    if (!promptInput.trim() || promptInput.length < 3) {
      setPreviewError('Please enter at least 3 characters');
      return;
    }

    setPreviewLoading(true);
    setPreviewError('');
    setPreviewText('');

    try {
      const response = await axios.post<{ preview: string }>(`${API}/preview/generate`, {
        prompt: promptInput
      });
      setPreviewText(response.data.preview);
      setShowPreview(true);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 429) {
          setPreviewError('You\'ve reached the preview limit. Sign up for unlimited story generation!');
        } else if (error.response?.data?.detail) {
          setPreviewError(error.response.data.detail);
        } else {
          setPreviewError('Something went wrong. Please try again.');
        }
      } else {
        setPreviewError('Something went wrong. Please try again.');
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    const init = async (): Promise<void> => {
      // Check auth
      try {
        const response = await axios.get<UserAuth>(`${API}/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
        navigate('/home');
        return;
      } catch (error) {
        setUser(null);
      }

      // Load featured stories and series for the landing page
      try {
        const [storiesRes, seriesRes] = await Promise.all([
          axios.get<FeaturedStory[]>(`${API}/stories/featured?limit=4`),
          axios.get<{ series: FeaturedSeries[] }>(`${API}/v1/series/public/featured?limit=4`, { withCredentials: true })
        ]);
        setFeaturedStories(storiesRes.data);
        setFeaturedSeries(seriesRes.data.series || []);
      } catch (error) {
        console.error('Failed to load featured content:', error);
      }

      setLoading(false);
    };
    init();
  }, [navigate]);

  // Featured categories for landing page
  const categories: Category[] = [
    { id: 'adventure', label: 'Adventure', icon: Map, color: '#10B981' },
    { id: 'fantasy', label: 'Fantasy', icon: Wand2, color: '#8B5CF6' },
    { id: 'bedtime', label: 'Bedtime', icon: Moon, color: '#6366F1' },
    { id: 'animals', label: 'Animals', icon: PawPrint, color: '#F59E0B' },
    { id: 'family', label: 'Family', icon: Heart, color: 'var(--st-primary)' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--st-bg-page)' }}>
        <div className="text-center">
          <Logo size={140} showText animate className="mx-auto mb-4" />
          <Sparkles className="w-8 h-8 mx-auto animate-spin" style={{ color: 'var(--st-primary)' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--st-bg-page)' }}>
      {/* Navigation Header */}
      <header className="sticky top-0 z-50 backdrop-blur-sm" style={{ background: 'color-mix(in srgb, var(--st-bg-surface) 95%, transparent)', borderBottom: '1px solid var(--st-border-light)' }}>
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
            <Wordmark size="md" />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/home')}
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:bg-black/5 dark:hover:bg-white/10"
              style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
            >
              <Compass className="w-4 h-4" style={{ color: 'var(--st-primary)' }} />
              Explore
            </button>
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 rounded-lg text-sm font-bold transition-all"
              style={{
                background: 'linear-gradient(135deg, var(--st-gradient-primary-end) 0%, var(--st-gradient-primary-start) 100%)',
                color: 'white',
                fontFamily: 'Quicksand, sans-serif'
              }}
            >
              Sign In
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section - Clear Value Proposition */}
      <section className="hero-pattern py-12 md:py-20 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            {/* Clear Headline - No logo here, Suno-style */}
            <h1
              className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3 animate-fade-in-up"
              style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
            >
              Create Personalized
              <br />
              <span style={{ color: 'var(--st-primary)' }}>Illustrated Storybooks</span>
            </h1>

            {/* Clear Subheadline */}
            <p
              className="text-base md:text-lg mb-6 max-w-xl mx-auto animate-fade-in-up"
              style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)', animationDelay: '100ms' }}
            >
              From picture books to chapter books - describe your story idea and our AI
              creates a complete book with custom illustrations in minutes.
            </p>

            {/* Typewriter Carousel */}
            <div className="mb-4 animate-fade-in-up" style={{ animationDelay: '150ms' }}>
              <TypewriterCarousel onPromptSelect={handlePromptSelect} />
            </div>

            {/* Prompt Input */}
            <div className="max-w-lg mx-auto mb-6 animate-fade-in-up" style={{ animationDelay: '200ms' }}>
              <div
                className="flex items-center gap-2 rounded-xl p-2 shadow-md hero-input-container"
              >
                <input
                  type="text"
                  value={promptInput}
                  onChange={(e) => {
                    setPromptInput(e.target.value);
                    setPreviewError('');
                  }}
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => e.key === 'Enter' && !previewLoading && generatePreview()}
                  placeholder="A brave girl who finds a magic garden..."
                  className="flex-1 px-3 py-2.5 text-sm bg-transparent outline-none"
                  style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
                  maxLength={500}
                />
                <button
                  onClick={generatePreview}
                  disabled={previewLoading || !promptInput.trim()}
                  className="px-5 py-2.5 rounded-lg text-sm font-bold transition-all flex items-center gap-2 disabled:opacity-50"
                  style={{
                    background: 'linear-gradient(135deg, var(--st-gradient-primary-end) 0%, var(--st-gradient-primary-start) 100%)',
                    color: 'white',
                    fontFamily: 'Quicksand, sans-serif'
                  }}
                >
                  {previewLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Wand2 className="w-4 h-4" />
                  )}
                  Try Free
                </button>
              </div>
              {previewError && (
                <p className="mt-2 text-xs text-red-500" style={{ fontFamily: 'Quicksand, sans-serif' }}>
                  {previewError}
                </p>
              )}
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center justify-center gap-4 text-xs animate-fade-in-up" style={{ animationDelay: '300ms' }}>
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full shadow-sm hero-trust-badge">
                <CheckCircle className="w-3.5 h-3.5" style={{ color: '#10B981' }} />
                <span style={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 600 }}>Free to Start</span>
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full shadow-sm hero-trust-badge">
                <BookOpen className="w-3.5 h-3.5" style={{ color: 'var(--st-primary)' }} />
                <span style={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 600 }}>All Ages</span>
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full shadow-sm hero-trust-badge">
                <Clock className="w-3.5 h-3.5" style={{ color: '#8B5CF6' }} />
                <span style={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 600 }}>Ready in Minutes</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="py-16 px-4 what-you-get-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold mb-3"
              style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
            >
              What You Get
            </h2>
            <p
              className="text-base max-w-xl mx-auto"
              style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-body)' }}
            >
              Every story is a complete illustrated book - from short picture books to longer chapter books
            </p>
          </div>

          {/* Main Features - Alternating Layout */}
          <div className="space-y-6 mb-12">
            {/* Row 1: 3 cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {([
                {
                  icon: BookOpen,
                  title: 'AI-Written Stories',
                  description: 'Engaging narratives tailored to your prompt and chosen age range',
                  color: 'var(--st-primary)',
                  gradientVar: '--st-feature-orange',
                  iconBg: 'linear-gradient(135deg, var(--st-gradient-primary-end) 0%, var(--st-gradient-primary-start) 100%)'
                },
                {
                  icon: ImageIcon,
                  title: 'Custom Illustrations',
                  description: 'Beautiful AI-generated artwork for every page of your story',
                  color: '#8B5CF6',
                  gradientVar: '--st-feature-purple',
                  iconBg: 'linear-gradient(135deg, #A78BFA 0%, #8B5CF6 100%)'
                },
                {
                  icon: Palette,
                  title: 'Art Style Choices',
                  description: 'Watercolor, storybook, cartoon, anime - pick your favorite style',
                  color: '#10B981',
                  gradientVar: '--st-feature-green',
                  iconBg: 'linear-gradient(135deg, #34D399 0%, #10B981 100%)'
                }
              ] as { icon: LucideIcon; title: string; description: string; color: string; gradientVar: string; iconBg: string }[]).map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="feature-card-enhanced">
                    <div className="feature-card-inner" style={{ background: `var(${feature.gradientVar})` }}>
                      <div className="feature-icon-wrapper" style={{ background: feature.iconBg }}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <h3
                        className="text-lg font-bold mb-2"
                        style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
                      >
                        {feature.title}
                      </h3>
                      <p
                        className="text-sm"
                        style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-body)' }}
                      >
                        {feature.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Row 2: 4 premium feature cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5">
              {([
                {
                  icon: Volume2,
                  title: 'Audio Narration',
                  description: 'Listen to your stories with natural AI voice narration',
                  color: '#3B82F6',
                  gradientVar: '--st-feature-blue',
                  iconBg: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
                  premium: true
                },
                {
                  icon: Video,
                  title: 'Animated Pages',
                  description: 'Bring illustrations to life with AI-generated video animations',
                  color: '#EC4899',
                  gradientVar: '--st-feature-pink',
                  iconBg: 'linear-gradient(135deg, #F472B6 0%, #EC4899 100%)',
                  premium: true
                },
                {
                  icon: Camera,
                  title: 'Photo Transform',
                  description: 'Turn any photo into an illustrated story character',
                  color: '#F59E0B',
                  gradientVar: '--st-feature-yellow',
                  iconBg: 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)',
                  premium: true
                },
                {
                  icon: Zap,
                  title: 'Premium Quality',
                  description: 'Enhanced AI image generation for stunning, detailed illustrations',
                  color: '#8B5CF6',
                  gradientVar: '--st-feature-purple',
                  iconBg: 'linear-gradient(135deg, #A78BFA 0%, #8B5CF6 100%)',
                  premium: true
                }
              ] as { icon: LucideIcon; title: string; description: string; color: string; gradientVar: string; iconBg: string; premium?: boolean }[]).map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="feature-card-enhanced">
                    <div className="feature-card-inner" style={{ background: `var(${feature.gradientVar})` }}>
                      {feature.premium && (
                        <span className="premium-badge-enhanced">
                          <Star className="w-3 h-3" />
                          PREMIUM
                        </span>
                      )}
                      <div className="feature-icon-wrapper" style={{ background: feature.iconBg }}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <h3
                        className="text-lg font-bold mb-2"
                        style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
                      >
                        {feature.title}
                      </h3>
                      <p
                        className="text-sm"
                        style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-body)' }}
                      >
                        {feature.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Visual Example - Open Book Mockup */}
          <div className="idea-to-book-section">
            <div className="flex flex-col lg:flex-row items-center gap-8">
              {/* Left: Text Content */}
              <div className="flex-1 text-center lg:text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4" style={{ background: 'var(--st-border-light)' }}>
                  <Wand2 className="w-4 h-4" style={{ color: 'var(--st-primary)' }} />
                  <span className="text-xs font-bold" style={{ color: '#D97706', fontFamily: 'Quicksand, sans-serif' }}>
                    AI-POWERED MAGIC
                  </span>
                </div>
                <h3
                  className="text-2xl md:text-3xl font-bold mb-4"
                  style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
                >
                  From Idea to{' '}
                  <span style={{ color: 'var(--st-primary)' }}>Illustrated Book</span>
                </h3>
                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3 text-left">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--st-border-light)' }}>
                      <PenLine className="w-4 h-4" style={{ color: 'var(--st-primary)' }} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold" style={{ color: 'var(--st-text-heading)', fontFamily: 'Quicksand, sans-serif' }}>
                        You write:
                      </p>
                      <p className="text-sm italic" style={{ color: 'var(--st-text-body)', fontFamily: 'Quicksand, sans-serif' }}>
                        "A little elephant who loses his favorite blanket"
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 text-left">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#DCFCE7' }}>
                      <Sparkles className="w-4 h-4" style={{ color: '#22C55E' }} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold" style={{ color: 'var(--st-text-heading)', fontFamily: 'Quicksand, sans-serif' }}>
                        We create:
                      </p>
                      <p className="text-sm" style={{ color: 'var(--st-text-body)', fontFamily: 'Quicksand, sans-serif' }}>
                        A complete illustrated storybook about his adventure to find it!
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/login')}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-base font-bold transition-all hover:scale-[1.02]"
                  style={{
                    background: 'linear-gradient(135deg, var(--st-gradient-primary-end) 0%, var(--st-gradient-primary-start) 100%)',
                    color: 'white',
                    fontFamily: 'Chewy, cursive'
                  }}
                >
                  <Sparkles className="w-5 h-5" />
                  Create Your First Story
                </button>
              </div>

              {/* Right: Open Book Visual */}
              <div className="flex-1 flex justify-center">
                <div className="open-book-real">
                  <img
                    src="/images/landing/book-spread.png"
                    alt="Example storybook spread with illustrations"
                    className="open-book-image"
                  />
                  {/* Floating sparkle */}
                  <div className="floating-star star-1">
                    <Sparkles className="w-5 h-5" style={{ color: 'var(--st-primary)' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-14 px-4" style={{ background: 'var(--st-bg-page)' }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2
              className="text-2xl md:text-3xl font-bold mb-2"
              style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
            >
              How It Works
            </h2>
            <p
              className="text-sm"
              style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
            >
              Three simple steps to your personalized storybook
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {([
              {
                icon: PenLine,
                title: 'Describe Your Story',
                description: 'Enter your idea, pick an age range, and choose your art style',
                color: 'var(--st-primary)'
              },
              {
                icon: Wand2,
                title: 'AI Creates Magic',
                description: 'Our AI writes the story and generates beautiful illustrations for each page',
                color: '#8B5CF6'
              },
              {
                icon: Play,
                title: 'Read & Enjoy',
                description: 'Read together, listen with audio narration, or share with loved ones',
                color: '#10B981'
              }
            ] as HowItWorksStep[]).map((step, index) => {
              const Icon = step.icon;
              return (
                <div
                  key={index}
                  className="text-center rounded-xl p-6 shadow-sm"
                  style={{ background: 'var(--st-card-bg)', border: '1px solid var(--st-card-border)' }}
                >
                  {/* Step number */}
                  <div
                    className="w-8 h-8 mx-auto mb-3 rounded-full flex items-center justify-center text-sm font-bold"
                    style={{ background: 'var(--st-border-light)', color: 'var(--st-primary)', fontFamily: 'Chewy, cursive' }}
                  >
                    {index + 1}
                  </div>
                  <div
                    className="w-14 h-14 mx-auto mb-3 rounded-xl flex items-center justify-center"
                    style={{ background: `${step.color}15` }}
                  >
                    <Icon className="w-7 h-7" style={{ color: step.color }} />
                  </div>
                  <h3
                    className="text-base font-bold mb-1"
                    style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
                  >
                    {step.title}
                  </h3>
                  <p
                    className="text-xs"
                    style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
                  >
                    {step.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Stories Section */}
      {featuredStories.length > 0 && (
        <section className="py-14 px-4" style={{ background: 'var(--st-bg-surface)' }}>
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h2
                className="text-2xl md:text-3xl font-bold mb-2"
                style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
              >
                See What Others Have Created
              </h2>
              <p
                className="text-sm"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
              >
                Join our community of storytellers
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {featuredStories.map((story) => (
                <div
                  key={story.id}
                  onClick={() => navigate(`/story/${story.id}`)}
                  className="rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all hover:scale-[1.02] cursor-pointer"
                  style={{ background: 'var(--st-card-bg)', border: '1px solid var(--st-card-border)' }}
                >
                  <div className="h-28 sm:h-32 overflow-hidden" style={{ background: 'var(--st-feature-orange)' }}>
                    {story.thumbnail ? (
                      <img
                        src={getImageUrl(story.thumbnail) || undefined}
                        alt={story.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen className="w-8 h-8" style={{ color: 'var(--st-primary)' }} />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                      <span className="px-1.5 py-0.5 rounded-full text-xs font-semibold" style={{ background: 'var(--st-border-light)', color: 'var(--st-primary)', fontSize: '10px' }}>
                        {story.category}
                      </span>
                      <span className="text-xs" style={{ color: 'var(--st-text-muted)', fontSize: '10px' }}>{getBookTypeLabel(story.age_range)}</span>
                    </div>
                    <h3 className="font-bold text-xs mb-1.5 line-clamp-1" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                      {story.title}
                    </h3>
                    <div className="flex items-center justify-between text-xs" style={{ color: 'var(--st-text-muted)' }}>
                      <div className="flex items-center gap-1">
                        {story.author_picture ? (
                          <img src={getImageUrl(story.author_picture) || undefined} alt={`${story.author_name || 'Author'} avatar`} className="w-3.5 h-3.5 rounded-full" />
                        ) : (
                          <User className="w-3 h-3" />
                        )}
                        <span className="truncate max-w-[60px]" style={{ fontFamily: 'Quicksand, sans-serif', fontSize: '10px' }}>{story.author_name || 'Anonymous'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Heart className="w-3 h-3" />
                        <span style={{ fontSize: '10px' }}>{story.favorites_count || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-6">
              <button
                onClick={() => navigate('/home')}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all shadow-sm"
                style={{
                  background: 'var(--st-bg-surface)',
                  color: 'var(--st-primary)',
                  fontFamily: 'Quicksand, sans-serif',
                  border: '1px solid var(--st-border-light)'
                }}
              >
                View All Stories
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Book Series Section - Advanced Feature */}
      <section className="py-16 px-4 series-section-bg overflow-hidden">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 series-badge">
              <Library className="w-4 h-4" style={{ color: '#6366F1' }} />
              <span className="text-xs font-bold tracking-wide" style={{ color: '#4F46E5', fontFamily: 'Quicksand, sans-serif' }}>
                PREMIUM FEATURE
              </span>
            </div>
            <h2
              className="text-3xl md:text-4xl font-bold mb-3"
              style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
            >
              Build Your Own{' '}
              <span className="series-title-gradient">Book Series</span>
            </h2>
            <p
              className="text-base max-w-2xl mx-auto"
              style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-body)' }}
            >
              Create recurring characters that appear consistently across multiple stories.
              Build epic adventures that span entire book collections!
            </p>
          </div>

          {/* Visual Demo - Book Stack Concept */}
          <div className="flex flex-col lg:flex-row items-center gap-8 mb-10">
            {/* Left: Book Stack Visual */}
            <div className="flex-1 flex justify-center">
              <div className="book-series-stack-real">
                {/* Book 3 - Back */}
                <div className="stack-book-item book-pos-3">
                  <div className="book-spine-item spine-3"></div>
                  <div className="book-cover-item">
                    <img
                      src="/images/landing/series-cover-3.png"
                      alt="Series Book 3"
                    />
                  </div>
                </div>
                {/* Book 2 - Middle */}
                <div className="stack-book-item book-pos-2">
                  <div className="book-spine-item spine-2"></div>
                  <div className="book-cover-item">
                    <img
                      src="/images/landing/series-cover-2.png"
                      alt="Series Book 2"
                    />
                  </div>
                </div>
                {/* Book 1 - Front */}
                <div className="stack-book-item book-pos-1">
                  <div className="book-spine-item spine-1"></div>
                  <div className="book-cover-item">
                    <img
                      src="/images/landing/series-cover.png"
                      alt="The Broken Bridge - Series Book 1"
                    />
                  </div>
                </div>
                {/* Character badge */}
                <div className="series-characters-badge">
                  <Users className="w-4 h-4" style={{ color: '#6366F1' }} />
                  <span>Same characters in every book!</span>
                </div>
              </div>
            </div>

            {/* Right: Feature List */}
            <div className="flex-1 space-y-4">
              {([
                {
                  icon: Users,
                  title: 'Consistent Characters',
                  description: 'Your characters look the same in every illustration - same face, same style, every time',
                  color: '#6366F1'
                },
                {
                  icon: BookOpen,
                  title: 'Connected Adventures',
                  description: 'Build multi-book story arcs where one adventure leads to the next',
                  color: '#8B5CF6'
                },
                {
                  icon: Globe,
                  title: 'Persistent Worlds',
                  description: 'Create settings and locations your characters can revisit in future stories',
                  color: '#10B981'
                }
              ] as SeriesFeature[]).map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="flex items-start gap-4 p-4 rounded-xl transition-all series-feature-card"
                  >
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: `${feature.color}15` }}
                    >
                      <Icon className="w-6 h-6" style={{ color: feature.color }} />
                    </div>
                    <div>
                      <h3
                        className="text-lg font-bold mb-1"
                        style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
                      >
                        {feature.title}
                      </h3>
                      <p
                        className="text-sm"
                        style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-body)' }}
                      >
                        {feature.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Featured Series */}
          {featuredSeries.length > 0 && (
            <div className="mb-10">
              <h3
                className="text-xl font-bold mb-6 text-center"
                style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
              >
                Explore Community Series
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {featuredSeries.map((series) => (
                  <div
                    key={series.id}
                    onClick={() => navigate(`/series/public/${series.id}`)}
                    className="series-card group cursor-pointer"
                  >
                    {/* Book Stack Effect */}
                    <div className="series-card-stack">
                      <div className="stack-layer stack-3"></div>
                      <div className="stack-layer stack-2"></div>
                      <div className="stack-layer stack-1">
                        <div className="series-cover-image">
                          {series.cover_image_url ? (
                            <img
                              src={getImageUrl(series.cover_image_url) || undefined}
                              alt={series.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 100%)' }}>
                              <Library className="w-10 h-10" style={{ color: '#818CF8' }} />
                            </div>
                          )}
                          {/* Book count badge */}
                          <div className="series-book-count">
                            <BookOpen className="w-3.5 h-3.5" />
                            <span>{series.book_count || 0}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card Content */}
                    <div className="series-card-content">
                      <h4 className="font-bold text-sm mb-2 line-clamp-1 group-hover:text-indigo-600 transition-colors" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                        {series.title}
                      </h4>

                      {/* Author Row */}
                      <div className="flex items-center gap-2 mb-2">
                        {series.author_picture ? (
                          <img src={getImageUrl(series.author_picture) || undefined} alt={`${series.author_name || 'Author'} avatar`} className="w-5 h-5 rounded-full" />
                        ) : (
                          <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: '#E0E7FF' }}>
                            <User className="w-3 h-3" style={{ color: '#6366F1' }} />
                          </div>
                        )}
                        <span className="text-xs truncate" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-body)' }}>
                          {series.author_name || 'Anonymous'}
                        </span>
                      </div>

                      {/* Character Avatars */}
                      {series.characters && series.characters.length > 0 && (
                        <div className="flex items-center gap-1">
                          <div className="flex -space-x-2">
                            {series.characters.slice(0, 3).map((char, idx) => (
                              <div
                                key={idx}
                                className="w-6 h-6 rounded-full border-2 border-white overflow-hidden"
                                style={{ background: '#E0E7FF' }}
                              >
                                {char.reference_image_url ? (
                                  <img
                                    src={getImageUrl(char.reference_image_url) || undefined}
                                    alt={char.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <User className="w-3 h-3" style={{ color: '#6366F1' }} />
                                  </div>
                                )}
                              </div>
                            ))}
                            {series.characters.length > 3 && (
                              <div
                                className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-[10px] font-bold"
                                style={{ background: '#E0E7FF', color: '#6366F1' }}
                              >
                                +{series.characters.length - 3}
                              </div>
                            )}
                          </div>
                          <span className="text-[10px] ml-1" style={{ color: 'var(--st-text-muted)' }}>
                            {series.characters.length} character{series.characters.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="text-center">
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl text-lg font-bold transition-all hover:scale-[1.02] series-cta-btn"
            >
              <Library className="w-6 h-6" />
              Start Your Book Series
            </button>
          </div>
        </div>
      </section>

      {/* Print Your Book Section - Coming Soon */}
      <PrintComingSoonSection />

      {/* Mobile App Section */}
      <section className="py-16 px-4 mobile-app-section-bg">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-10">
            {/* Left: Phone Screenshot */}
            <div className="flex-1 flex justify-center">
              <div className="relative">
                <img
                  src="/images/landing/storyteller-phone-screen.png"
                  alt="Tellor mobile app"
                  className="max-w-[300px] w-full h-auto"
                  style={{
                    filter: 'drop-shadow(0 25px 50px rgba(0, 0, 0, 0.3))'
                  }}
                />
                {/* Floating badges */}
                <div
                  className="absolute -right-4 top-24 px-3 py-2 rounded-xl shadow-lg flex items-center gap-2"
                  style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}
                >
                  <BookOpen className="w-4 h-4" style={{ color: 'var(--st-primary)' }} />
                  <span className="text-xs font-semibold" style={{ color: 'var(--st-text-heading)', fontFamily: 'Quicksand, sans-serif' }}>Read anywhere</span>
                </div>
                <div
                  className="absolute -left-4 bottom-48 px-3 py-2 rounded-xl shadow-lg flex items-center gap-2"
                  style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}
                >
                  <Heart className="w-4 h-4" style={{ color: '#EC4899' }} />
                  <span className="text-xs font-semibold" style={{ color: 'var(--st-text-heading)', fontFamily: 'Quicksand, sans-serif' }}>Save favorites</span>
                </div>
              </div>
            </div>

            {/* Right: Content */}
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 mobile-app-badge">
                <Smartphone className="w-4 h-4 mobile-app-badge-icon" />
                <span className="text-xs font-bold tracking-wide mobile-app-badge-text" style={{ fontFamily: 'Quicksand, sans-serif' }}>
                  MOBILE APP
                </span>
              </div>
              <h2
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
              >
                Stories On The Go
              </h2>
              <p
                className="text-base mb-6"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-body)' }}
              >
                Take your library everywhere with our mobile app. Read stories, discover new ones,
                and manage your favorites - all from your phone or tablet.
              </p>

              {/* Available Features */}
              <div className="space-y-3 mb-6">
                {[
                  { icon: BookOpen, text: 'Read your entire story library', color: 'var(--st-primary)' },
                  { icon: Compass, text: 'Explore and discover new stories', color: '#8B5CF6' },
                  { icon: Heart, text: 'Save favorites for quick access', color: '#EC4899' },
                  { icon: Volume2, text: 'Listen with audio narration', color: '#3B82F6' },
                  { icon: Video, text: 'Watch animated story pages', color: '#EC4899' },
                  { icon: Download, text: 'Download stories for offline reading', color: '#F59E0B' },
                ].map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div key={index} className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ background: `${feature.color}15` }}
                      >
                        <Icon className="w-4 h-4" style={{ color: feature.color }} />
                      </div>
                      <span
                        className="text-sm font-medium"
                        style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
                      >
                        {feature.text}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Coming Soon */}
              <div
                className="p-4 rounded-xl mb-6"
                style={{ background: 'var(--st-bg-surface)', border: '1px dashed var(--st-border-medium)' }}
              >
                <p
                  className="text-xs font-bold mb-2 flex items-center gap-2"
                  style={{ fontFamily: 'Quicksand, sans-serif', color: '#F59E0B' }}
                >
                  <Sparkles className="w-3 h-3" />
                  COMING SOON
                </p>
                <div className="flex flex-wrap gap-3">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                    <Wand2 className="w-3.5 h-3.5" style={{ color: 'var(--st-text-muted)' }} />
                    <span className="text-xs font-medium" style={{ color: 'var(--st-text-heading)', fontFamily: 'Quicksand, sans-serif' }}>Create stories on mobile</span>
                  </div>
                </div>
              </div>

              {/* App Store Buttons */}
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://apps.apple.com/us/app/storyteller-by-mytho-labs/id6758119772"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl transition-all hover:scale-[1.02]"
                  style={{
                    background: '#000',
                    color: 'white'
                  }}
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <div className="text-left">
                    <div className="text-[10px] opacity-80">Download on the</div>
                    <div className="text-sm font-semibold -mt-0.5">App Store</div>
                  </div>
                </a>
                <div
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl opacity-60 cursor-not-allowed"
                  style={{
                    background: '#000',
                    color: 'white'
                  }}
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z"/>
                  </svg>
                  <div className="text-left">
                    <div className="text-[10px] opacity-80">Coming Soon</div>
                    <div className="text-sm font-semibold -mt-0.5">Google Play</div>
                  </div>
                </div>
              </div>
              <p
                className="text-xs mt-3"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
              >
                Available now on iOS. Android coming soon!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-12 px-4" style={{ background: 'var(--st-bg-surface)' }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-6">
            <h2
              className="text-2xl md:text-3xl font-bold mb-2"
              style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
            >
              Stories for Every Imagination
            </h2>
            <p
              className="text-sm"
              style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
            >
              Explore different genres and themes
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((cat) => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => navigate(`/explore?category=${cat.id}`)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all hover:shadow-sm hover:scale-[1.02]"
                  style={{
                    background: 'var(--st-bg-surface)',
                    color: 'var(--st-text-heading)',
                    fontFamily: 'Quicksand, sans-serif',
                    border: '1px solid var(--st-border-light)'
                  }}
                >
                  <Icon className="w-4 h-4" style={{ color: cat.color }} />
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Simple Pricing Preview */}
      <section className="py-14 px-4" style={{ background: 'var(--st-bg-page)' }}>
        <div className="max-w-3xl mx-auto text-center">
          <h2
            className="text-2xl md:text-3xl font-bold mb-2"
            style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
          >
            Simple, Transparent Pricing
          </h2>
          <p
            className="text-sm mb-8"
            style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
          >
            Start free, upgrade when you're ready
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {/* Free */}
            <div
              className="rounded-xl p-6 text-center"
              style={{ background: 'var(--st-card-bg)', border: '1px solid var(--st-card-border)' }}
            >
              <div
                className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center"
                style={{ background: 'var(--st-border-light)' }}
              >
                <Sparkles className="w-6 h-6" style={{ color: 'var(--st-primary)' }} />
              </div>
              <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                Free Start
              </h3>
              <p className="text-2xl font-bold mb-2" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-primary)' }}>
                1 Credit
              </p>
              <p className="text-xs mb-4" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                Sign up and get 1 free credit to create your first illustrated story
              </p>
              <ul className="text-xs text-left space-y-2 mb-4" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#10B981' }} />
                  Full illustrated storybook
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#10B981' }} />
                  Multiple art styles
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#10B981' }} />
                  Share with family
                </li>
              </ul>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-2.5 rounded-lg font-semibold text-sm transition-all"
                style={{
                  background: 'linear-gradient(135deg, var(--st-gradient-primary-end) 0%, var(--st-gradient-primary-start) 100%)',
                  color: 'white',
                  fontFamily: 'Quicksand, sans-serif'
                }}
              >
                Get Started Free
              </button>
            </div>

            {/* Premium */}
            <div
              className="rounded-xl p-6 text-center relative overflow-hidden"
              style={{ background: 'var(--st-card-bg)', border: '2px solid var(--st-primary)' }}
            >
              <div
                className="absolute top-0 right-0 px-3 py-1 text-[10px] font-bold"
                style={{ background: 'linear-gradient(135deg, var(--st-gradient-primary-end) 0%, var(--st-gradient-primary-start) 100%)', color: 'white', borderBottomLeftRadius: '8px' }}
              >
                BEST VALUE
              </div>
              <div
                className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, var(--st-gradient-primary-end) 0%, var(--st-gradient-primary-start) 100%)' }}
              >
                <Crown className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                Premium
              </h3>
              <p className="text-2xl font-bold mb-2" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-primary)' }}>
                From $11.99<span className="text-sm font-normal">/mo</span>
              </p>
              <p className="text-xs mb-4" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                Monthly credits + premium features for serious storytellers
              </p>
              <ul className="text-xs text-left space-y-2 mb-4" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#10B981' }} />
                  5-15 stories/month
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#10B981' }} />
                  Audio narration & animated pages
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#10B981' }} />
                  Photo transform & Book Series
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#10B981' }} />
                  Premium image quality
                </li>
              </ul>
              <button
                onClick={() => navigate('/pricing')}
                className="w-full py-2.5 rounded-lg font-semibold text-sm transition-all"
                style={{
                  background: 'var(--st-bg-surface)',
                  color: 'var(--st-primary)',
                  fontFamily: 'Quicksand, sans-serif',
                  border: '1px solid var(--st-border-light)'
                }}
              >
                View Plans
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section
        className="py-16 px-4"
        style={{
          background: 'linear-gradient(135deg, var(--st-gradient-primary-end) 0%, var(--st-gradient-primary-start) 100%)'
        }}
      >
        <div className="max-w-xl mx-auto text-center">
          <h2
            className="text-2xl md:text-3xl font-bold mb-3 text-white"
            style={{ fontFamily: 'Chewy, cursive' }}
          >
            Ready to Create Magic?
          </h2>
          <p
            className="text-white/90 mb-6 text-sm"
            style={{ fontFamily: 'Quicksand, sans-serif' }}
          >
            Join thousands of families creating personalized stories.
            Sign up now and get your first story free!
          </p>
          <button
            onClick={() => navigate('/login')}
            className="px-8 py-3.5 rounded-xl text-lg font-bold transition-all hover:scale-[1.02]"
            style={{
              background: 'var(--st-bg-surface)',
              color: 'var(--st-primary)',
              fontFamily: 'Chewy, cursive',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
            }}
          >
            Get Started Free
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="py-8 text-center landing-footer"
      >
        <div className="flex items-center justify-center mb-2">
          <Wordmark size="lg" variant="light" />
        </div>
        <p
          className="text-xs text-white/70 mb-1"
          style={{ fontFamily: 'Quicksand, sans-serif' }}
        >
          Create personalized illustrated storybooks for your children
        </p>
        <p
          className="text-xs text-white/50 mb-4"
          style={{ fontFamily: 'Quicksand, sans-serif' }}
        >
          A Mytho Labs Product
        </p>
        {/* Footer Links */}
        <div className="flex flex-wrap justify-center gap-4 text-xs" style={{ fontFamily: 'Quicksand, sans-serif' }}>
          <button
            onClick={() => navigate('/help')}
            className="text-white/70 hover:text-white transition-colors"
          >
            Help & FAQ
          </button>
          <span className="text-white/30">|</span>
          <button
            onClick={() => navigate('/contact')}
            className="text-white/70 hover:text-white transition-colors"
          >
            Contact
          </button>
          <span className="text-white/30">|</span>
          <button
            onClick={() => navigate('/terms')}
            className="text-white/70 hover:text-white transition-colors"
          >
            Terms
          </button>
          <span className="text-white/30">|</span>
          <button
            onClick={() => navigate('/privacy')}
            className="text-white/70 hover:text-white transition-colors"
          >
            Privacy
          </button>
        </div>
      </footer>

      {/* Preview Modal */}
      {showPreview && previewText && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
          onClick={() => setShowPreview(false)}
        >
          <div
            className="rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden shadow-xl animate-fade-in-up"
            style={{ background: 'var(--st-bg-surface)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between p-4"
              style={{ borderBottom: '1px solid var(--st-border-light)' }}
            >
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" style={{ color: 'var(--st-primary)' }} />
                <h3
                  className="text-lg font-bold"
                  style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
                >
                  Your Story Preview
                </h3>
              </div>
              <button
                onClick={() => setShowPreview(false)}
                className="p-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5" style={{ color: 'var(--st-text-muted)' }} />
              </button>
            </div>

            <div className="p-5 overflow-y-auto" style={{ maxHeight: '50vh' }}>
              <p
                className="text-sm leading-relaxed whitespace-pre-wrap"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
              >
                {previewText}
              </p>
            </div>

            <div
              className="p-4 text-center"
              style={{ background: 'var(--st-bg-page)', borderTop: '1px solid var(--st-border-light)' }}
            >
              <p
                className="text-xs mb-3"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
              >
                Sign up to get beautiful illustrations and the full story!
              </p>
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-2.5 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-2 mx-auto"
                style={{
                  background: 'linear-gradient(135deg, var(--st-gradient-primary-end) 0%, var(--st-gradient-primary-start) 100%)',
                  color: 'white',
                  fontFamily: 'Chewy, cursive'
                }}
              >
                <Sparkles className="w-4 h-4" />
                Create Full Story with Illustrations
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
